import type { PriceModel } from "../src/types";
import type { Language } from "./languages";

// Curated comparisons reference exact model uids already present in the repository's
// catalog (see src/fallbackCatalog.ts), matched by exact string equality -- never through an
// app-side "latest" alias-resolution step (see resolveCuratedModels below, which hides rather
// than substitutes a missing uid). That guarantees a comparison only ever shows real, matched
// catalog entries.
//
// It does NOT guarantee the underlying model version behind a given endpoint id is fixed:
// providers can update what an endpoint id serves without changing its id (e.g. Mistral's
// "-latest" suffix is an explicit rolling alias, and DeepSeek's "deepseek-chat"/
// "deepseek-reasoner" are real production endpoint ids that are not version-dated either).
// Do not describe these uids as pinned/immutable model versions in copy or docs.
export type CuratedComparisonDefinition = {
  slug: string;
  modelUids: readonly string[];
};

export const CURATED_COMPARISONS: readonly CuratedComparisonDefinition[] = [
  {
    slug: "flagship-reasoning-models",
    modelUids: ["openai:gpt-5.6-sol", "anthropic:claude-sonnet-5", "google:gemini-3.8-flash"],
  },
  {
    slug: "budget-friendly-models",
    modelUids: ["openai:gpt-5.6-luna", "anthropic:claude-haiku-4-5", "google:gemini-3.5-flash-lite"],
  },
  {
    slug: "open-weight-value-models",
    modelUids: ["deepseek:deepseek-chat", "deepseek:deepseek-reasoner", "mistral:mistral-small-latest"],
  },
];

export function curatedComparisonSlugs(): string[] {
  return CURATED_COMPARISONS.map((comparison) => comparison.slug);
}

export function isCuratedComparisonSlug(value: string): boolean {
  return CURATED_COMPARISONS.some((comparison) => comparison.slug === value);
}

export function findCuratedComparison(slug: string): CuratedComparisonDefinition | undefined {
  return CURATED_COMPARISONS.find((comparison) => comparison.slug === slug);
}

export type ResolvedCuratedComparison = {
  available: PriceModel[];
  missingCount: number;
  isAvailable: boolean;
};

// Missing models are hidden rather than substituted, so a comparison never fabricates
// a price for a model the active catalog does not contain.
export function resolveCuratedModels(
  definition: CuratedComparisonDefinition,
  models: PriceModel[],
): ResolvedCuratedComparison {
  const resolved = definition.modelUids.map((uid) => models.find((model) => model.uid === uid));
  const available = resolved.filter((model): model is PriceModel => Boolean(model));
  return {
    available,
    missingCount: resolved.length - available.length,
    isAvailable: available.length >= 2,
  };
}

export type CuratedComparisonCopy = {
  title: string;
  rationale: string;
  workloadNote: string;
};

export type CompareDirectoryCopy = {
  kicker: string;
  title: string;
  intro: string;
  customTitle: string;
  customBody: string;
  customCta: string;
  unavailableNotice: string;
  missingModelsNotice: string;
  costDifferenceLabel: string;
  limitationsTitle: string;
  // Discloses that a comparison's model ids are exact catalog matches, not a guarantee that the
  // underlying model version behind a provider's endpoint id stays fixed over time.
  versionNotice: string;
  comparisons: readonly [CuratedComparisonCopy, CuratedComparisonCopy, CuratedComparisonCopy];
};

const copy: Record<Language, CompareDirectoryCopy> = {
  en: {
    kicker: "Curated comparisons",
    title: "AI model comparisons",
    intro: "Start from a ready-made comparison, or build your own from the pricing table.",
    customTitle: "Build a custom comparison",
    customBody: "Pick two or three models from the pricing table to create a shareable link with your own workload.",
    customCta: "Choose models",
    unavailableNotice: "This comparison is temporarily unavailable because fewer than two of its models are in the current catalog.",
    missingModelsNotice: "not currently in the catalog and hidden below",
    costDifferenceLabel: "Cost difference at this workload",
    limitationsTitle: "Limitations",
    versionNotice: "Model ids in this comparison are exact catalog matches, not a guarantee that the underlying model version behind a provider's endpoint id stays fixed \u2014 providers can update what an id serves over time. Prices reflect the current catalog snapshot, not a verified quality or latency ranking.",
    comparisons: [
      {
        title: "Flagship reasoning models",
        rationale: "High-capability, general-purpose models for production workloads that need strong reasoning.",
        workloadNote: "Workload defaults assume a reasoning-heavy assistant with moderate prompt caching.",
      },
      {
        title: "Budget-friendly models",
        rationale: "Lower-cost models suited to high-volume, latency-sensitive tasks like classification or short replies.",
        workloadNote: "Workload defaults assume high request volume with short prompts and outputs.",
      },
      {
        title: "Open-weight and value models",
        rationale: "Cost-efficient and open-weight options for teams optimizing spend over peak capability.",
        workloadNote: "Workload defaults assume a cost-sensitive coding or batch-processing workload.",
      },
    ],
  },
  ja: {
    kicker: "厳選比較",
    title: "AIモデル比較",
    intro: "用意された比較から始めるか、料金表から独自の比較を作成できます。",
    customTitle: "独自の比較を作成",
    customBody: "料金表から2〜3モデルを選び、独自の利用条件でリンクを共有できます。",
    customCta: "モデルを選ぶ",
    unavailableNotice: "この比較は、対象モデルのうち2つ未満しか現在のカタログに存在しないため、一時的に利用できません。",
    missingModelsNotice: "現在カタログに存在しないため、以下では非表示です",
    costDifferenceLabel: "この利用条件でのコスト差",
    limitationsTitle: "制約事項",
    versionNotice: "この比較のモデルIDはカタログ内の完全一致であり、プロバイダーのエンドポイントIDが指す実際のモデルバージョンが固定されていることを保証するものではありません（プロバイダーは同じIDのまま提供内容を更新できます）。価格は現在のカタログのスナップショットであり、検証済みの品質・レイテンシ順位ではありません。",
    comparisons: [
      {
        title: "フラッグシップ推論モデル",
        rationale: "高い推論力が必要な本番ワークロード向けの高性能な汎用モデルです。",
        workloadNote: "既定の利用条件は、推論中心のアシスタントで適度なキャッシュ利用を想定しています。",
      },
      {
        title: "低コストモデル",
        rationale: "分類や短い応答など、大量かつ低レイテンシが求められる用途向けの低価格モデルです。",
        workloadNote: "既定の利用条件は、短いプロンプトと出力で高いリクエスト量を想定しています。",
      },
      {
        title: "オープンウェイト・価格重視モデル",
        rationale: "最高性能よりコスト効率を重視するチーム向けの、オープンウェイトや低価格な選択肢です。",
        workloadNote: "既定の利用条件は、コスト重視のコーディングやバッチ処理を想定しています。",
      },
    ],
  },
  fr: {
    kicker: "Comparatifs sélectionnés",
    title: "Comparatifs de modèles IA",
    intro: "Partez d’un comparatif prêt à l’emploi ou créez le vôtre depuis le tableau des tarifs.",
    customTitle: "Créer un comparatif personnalisé",
    customBody: "Choisissez deux ou trois modèles dans le tableau des tarifs pour créer un lien partageable avec votre propre charge de travail.",
    customCta: "Choisir des modèles",
    unavailableNotice: "Ce comparatif est temporairement indisponible car moins de deux de ses modèles figurent dans le catalogue actuel.",
    missingModelsNotice: "absents du catalogue actuel et masqués ci-dessous",
    costDifferenceLabel: "Écart de coût pour cette charge de travail",
    limitationsTitle: "Limites",
    versionNotice: "Les identifiants de modèles de ce comparatif correspondent exactement au catalogue, mais ne garantissent pas que la version du modèle derrière l'identifiant d'un fournisseur reste fixe \u2014 les fournisseurs peuvent faire évoluer ce qu'un identifiant sert. Les prix reflètent l'instantané actuel du catalogue, pas un classement vérifié de qualité ou de latence.",
    comparisons: [
      {
        title: "Modèles phares de raisonnement",
        rationale: "Modèles généralistes très performants pour les charges de production exigeant un raisonnement solide.",
        workloadNote: "Charge par défaut : assistant à fort raisonnement avec cache de prompt modéré.",
      },
      {
        title: "Modèles économiques",
        rationale: "Modèles à faible coût adaptés aux tâches à fort volume et sensibles à la latence, comme la classification.",
        workloadNote: "Charge par défaut : volume de requêtes élevé avec prompts et sorties courts.",
      },
      {
        title: "Modèles ouverts et économiques",
        rationale: "Options à poids ouverts et économiques pour les équipes qui privilégient le coût à la capacité maximale.",
        workloadNote: "Charge par défaut : code ou traitement par lots sensible au coût.",
      },
    ],
  },
  de: {
    kicker: "Ausgewählte Vergleiche",
    title: "KI-Modellvergleiche",
    intro: "Starten Sie mit einem vorgefertigten Vergleich oder erstellen Sie Ihren eigenen aus der Preistabelle.",
    customTitle: "Eigenen Vergleich erstellen",
    customBody: "Wählen Sie zwei oder drei Modelle aus der Preistabelle, um einen teilbaren Link mit Ihrer eigenen Auslastung zu erstellen.",
    customCta: "Modelle auswählen",
    unavailableNotice: "Dieser Vergleich ist vorübergehend nicht verfügbar, da weniger als zwei seiner Modelle im aktuellen Katalog enthalten sind.",
    missingModelsNotice: "derzeit nicht im Katalog und unten ausgeblendet",
    costDifferenceLabel: "Kostenunterschied bei dieser Auslastung",
    limitationsTitle: "Einschränkungen",
    versionNotice: "Modell-IDs in diesem Vergleich sind exakte Katalogtreffer, aber keine Garantie dafür, dass die Modellversion hinter der Endpunkt-ID eines Anbieters unverändert bleibt \u2014 Anbieter können ändern, was eine ID liefert. Preise spiegeln den aktuellen Katalogstand wider, kein geprüftes Qualitäts- oder Latenzranking.",
    comparisons: [
      {
        title: "Leistungsstarke Vorzeigemodelle",
        rationale: "Leistungsstarke Allzweckmodelle für Produktions-Workloads mit hohem Anspruch an logisches Schließen.",
        workloadNote: "Standard-Workload: schlussfolgerungsintensiver Assistent mit moderatem Prompt-Caching.",
      },
      {
        title: "Preisgünstige Modelle",
        rationale: "Kostengünstige Modelle für hochvolumige, latenzsensible Aufgaben wie Klassifikation oder kurze Antworten.",
        workloadNote: "Standard-Workload: hohes Anfragevolumen mit kurzen Prompts und Ausgaben.",
      },
      {
        title: "Offene Gewichte und Preis-Leistung",
        rationale: "Kosteneffiziente und offene Modelle für Teams, die Ausgaben vor Spitzenleistung priorisieren.",
        workloadNote: "Standard-Workload: kostensensitives Coding oder Batch-Verarbeitung.",
      },
    ],
  },
  pt: {
    kicker: "Comparações selecionadas",
    title: "Comparações de modelos de IA",
    intro: "Comece por uma comparação pronta ou crie a sua a partir da tabela de preços.",
    customTitle: "Criar uma comparação personalizada",
    customBody: "Escolha dois ou três modelos na tabela de preços para criar um link compartilhável com sua própria carga de trabalho.",
    customCta: "Escolher modelos",
    unavailableNotice: "Esta comparação está temporariamente indisponível porque menos de dois de seus modelos estão no catálogo atual.",
    missingModelsNotice: "não estão atualmente no catálogo e ficam ocultos abaixo",
    costDifferenceLabel: "Diferença de custo nesta carga de trabalho",
    limitationsTitle: "Limitações",
    versionNotice: "Os ids de modelo desta comparação são correspondências exatas do catálogo, não uma garantia de que a versão do modelo por trás do id de endpoint de um provedor permaneça fixa \u2014 provedores podem atualizar o que um id entrega. Os preços refletem o instantâneo atual do catálogo, não um ranking verificado de qualidade ou latência.",
    comparisons: [
      {
        title: "Modelos principais de raciocínio",
        rationale: "Modelos de uso geral de alta capacidade para cargas de produção que exigem raciocínio forte.",
        workloadNote: "Carga padrão: assistente com foco em raciocínio e cache de prompt moderado.",
      },
      {
        title: "Modelos econômicos",
        rationale: "Modelos de menor custo adequados a tarefas de alto volume e sensíveis à latência, como classificação.",
        workloadNote: "Carga padrão: alto volume de requisições com prompts e saídas curtos.",
      },
      {
        title: "Modelos de pesos abertos e custo-benefício",
        rationale: "Opções econômicas e de pesos abertos para equipes que priorizam custo sobre capacidade máxima.",
        workloadNote: "Carga padrão: código ou processamento em lote sensível a custo.",
      },
    ],
  },
  es: {
    kicker: "Comparaciones seleccionadas",
    title: "Comparaciones de modelos de IA",
    intro: "Empieza con una comparación lista o crea la tuya desde la tabla de precios.",
    customTitle: "Crear una comparación personalizada",
    customBody: "Elige dos o tres modelos de la tabla de precios para crear un enlace compartible con tu propia carga de trabajo.",
    customCta: "Elegir modelos",
    unavailableNotice: "Esta comparación no está disponible temporalmente porque menos de dos de sus modelos están en el catálogo actual.",
    missingModelsNotice: "no están actualmente en el catálogo y quedan ocultos abajo",
    costDifferenceLabel: "Diferencia de costo en esta carga de trabajo",
    limitationsTitle: "Limitaciones",
    versionNotice: "Los ids de modelo de esta comparación son coincidencias exactas del catálogo, no una garantía de que la versión del modelo detrás del id de endpoint de un proveedor permanezca fija \u2014 los proveedores pueden actualizar lo que entrega un id. Los precios reflejan la instantánea actual del catálogo, no un ranking verificado de calidad o latencia.",
    comparisons: [
      {
        title: "Modelos insignia de razonamiento",
        rationale: "Modelos generalistas de alta capacidad para cargas de producción que necesitan razonamiento sólido.",
        workloadNote: "Carga predeterminada: asistente centrado en razonamiento con caché de prompt moderada.",
      },
      {
        title: "Modelos económicos",
        rationale: "Modelos de menor costo adecuados para tareas de alto volumen y sensibles a la latencia, como clasificación.",
        workloadNote: "Carga predeterminada: alto volumen de solicitudes con prompts y salidas cortas.",
      },
      {
        title: "Modelos de pesos abiertos y de valor",
        rationale: "Opciones económicas y de pesos abiertos para equipos que priorizan el costo sobre la capacidad máxima.",
        workloadNote: "Carga predeterminada: código o procesamiento por lotes sensible al costo.",
      },
    ],
  },
  ar: {
    kicker: "مقارنات مختارة",
    title: "مقارنات نماذج الذكاء الاصطناعي",
    intro: "ابدأ بمقارنة جاهزة أو أنشئ مقارنتك الخاصة من جدول الأسعار.",
    customTitle: "إنشاء مقارنة مخصصة",
    customBody: "اختر نموذجين أو ثلاثة من جدول الأسعار لإنشاء رابط قابل للمشاركة بحمل العمل الخاص بك.",
    customCta: "اختيار النماذج",
    unavailableNotice: "هذه المقارنة غير متاحة مؤقتًا لأن أقل من نموذجين منها موجودان في الكتالوج الحالي.",
    missingModelsNotice: "غير موجودة حاليًا في الكتالوج ومخفية أدناه",
    costDifferenceLabel: "فرق التكلفة عند حمل العمل هذا",
    limitationsTitle: "القيود",
    versionNotice: "معرّفات النماذج في هذه المقارنة تطابق الكتالوج تمامًا، لكنها لا تضمن ثبات إصدار النموذج الفعلي خلف معرّف نقطة النهاية لدى المزوّد \u2014 يمكن للمزوّدين تحديث ما يقدّمه المعرّف نفسه. تعكس الأسعار لقطة الكتالوج الحالية، وليست ترتيبًا موثّقًا للجودة أو زمن الاستجابة.",
    comparisons: [
      {
        title: "نماذج الاستدلال الرائدة",
        rationale: "نماذج عامة عالية القدرة لأحمال الإنتاج التي تتطلب استدلالًا قويًا.",
        workloadNote: "الحمل الافتراضي: مساعد يعتمد على الاستدلال مع استخدام معتدل للتخزين المؤقت للمطالبات.",
      },
      {
        title: "نماذج اقتصادية",
        rationale: "نماذج منخفضة التكلفة مناسبة للمهام عالية الحجم والحساسة لزمن الاستجابة مثل التصنيف.",
        workloadNote: "الحمل الافتراضي: حجم طلبات مرتفع بمطالبات ومخرجات قصيرة.",
      },
      {
        title: "نماذج الأوزان المفتوحة والقيمة",
        rationale: "خيارات اقتصادية ومفتوحة الأوزان للفرق التي تُفضّل التكلفة على أقصى قدرة.",
        workloadNote: "الحمل الافتراضي: برمجة أو معالجة دفعية حساسة للتكلفة.",
      },
    ],
  },
  it: {
    kicker: "Confronti selezionati",
    title: "Confronti tra modelli IA",
    intro: "Parti da un confronto già pronto oppure creane uno personalizzato dalla tabella dei prezzi.",
    customTitle: "Crea un confronto personalizzato",
    customBody: "Scegli due o tre modelli dalla tabella dei prezzi per creare un link condivisibile con il tuo carico di lavoro.",
    customCta: "Scegli i modelli",
    unavailableNotice: "Questo confronto non è temporaneamente disponibile perché meno di due dei suoi modelli sono nel catalogo attuale.",
    missingModelsNotice: "non attualmente nel catalogo e nascosti qui sotto",
    costDifferenceLabel: "Differenza di costo per questo carico di lavoro",
    limitationsTitle: "Limitazioni",
    versionNotice: "Gli id dei modelli in questo confronto corrispondono esattamente al catalogo, ma non garantiscono che la versione del modello dietro l'id endpoint di un fornitore resti fissa \u2014 i fornitori possono aggiornare ciò che un id restituisce. I prezzi riflettono lo snapshot attuale del catalogo, non una classifica verificata di qualità o latenza.",
    comparisons: [
      {
        title: "Modelli di punta per il ragionamento",
        rationale: "Modelli generici ad alta capacità per carichi di produzione che richiedono un ragionamento solido.",
        workloadNote: "Carico predefinito: assistente incentrato sul ragionamento con caching dei prompt moderato.",
      },
      {
        title: "Modelli economici",
        rationale: "Modelli a basso costo adatti a compiti ad alto volume e sensibili alla latenza, come la classificazione.",
        workloadNote: "Carico predefinito: alto volume di richieste con prompt e output brevi.",
      },
      {
        title: "Modelli open-weight e convenienti",
        rationale: "Opzioni economiche e open-weight per team che privilegiano il costo rispetto alla massima capacità.",
        workloadNote: "Carico predefinito: codice o elaborazione batch sensibile al costo.",
      },
    ],
  },
  "zh-CN": {
    kicker: "精选对比",
    title: "AI 模型对比",
    intro: "可以从现成的对比开始，也可以从价格表中创建自己的对比。",
    customTitle: "创建自定义对比",
    customBody: "从价格表中选择两到三个模型，即可创建包含你自己用量假设的可分享链接。",
    customCta: "选择模型",
    unavailableNotice: "由于当前目录中可用的模型少于两个，此对比暂时不可用。",
    missingModelsNotice: "当前不在目录中，已在下方隐藏",
    costDifferenceLabel: "该用量下的成本差异",
    limitationsTitle: "局限性",
    versionNotice: "本对比中的模型 ID 是与目录的精确匹配，但不代表厂商端点 ID 背后的具体模型版本永久固定——厂商可以在不更换 ID 的情况下更新其实际提供的模型。价格反映的是当前目录快照，并非经过验证的质量或延迟排名。",
    comparisons: [
      {
        title: "旗舰推理模型",
        rationale: "适合需要强推理能力的生产工作负载的高性能通用模型。",
        workloadNote: "默认用量假设为以推理为主、具有适度提示缓存的助手场景。",
      },
      {
        title: "高性价比模型",
        rationale: "适合分类或短回复等高吞吐、低延迟场景的低成本模型。",
        workloadNote: "默认用量假设为提示词和输出较短、请求量较高的场景。",
      },
      {
        title: "开放权重与高性价比模型",
        rationale: "适合更看重成本而非峰值能力的团队的开放权重及高性价比选项。",
        workloadNote: "默认用量假设为对成本敏感的编码或批处理场景。",
      },
    ],
  },
  "zh-TW": {
    kicker: "精選比較",
    title: "AI 模型比較",
    intro: "可以從現成的比較開始，也可以從價格表建立自己的比較。",
    customTitle: "建立自訂比較",
    customBody: "從價格表中選擇兩到三個模型，即可建立包含你自己用量假設的可分享連結。",
    customCta: "選擇模型",
    unavailableNotice: "由於目前目錄中可用的模型少於兩個，此比較暫時無法使用。",
    missingModelsNotice: "目前不在目錄中，已於下方隱藏",
    costDifferenceLabel: "此用量下的成本差異",
    limitationsTitle: "限制",
    versionNotice: "本比較中的模型 ID 為與目錄的精確比對，但不代表廠商端點 ID 背後的具體模型版本永久固定——廠商可在不更換 ID 的情況下更新其實際提供的模型。價格反映目前的目錄快照，並非經過驗證的品質或延遲排名。",
    comparisons: [
      {
        title: "旗艦推理模型",
        rationale: "適合需要強推理能力的生產工作負載的高效能通用模型。",
        workloadNote: "預設用量假設為以推理為主、具有適度提示快取的助理情境。",
      },
      {
        title: "高性價比模型",
        rationale: "適合分類或簡短回覆等高吞吐、低延遲情境的低成本模型。",
        workloadNote: "預設用量假設為提示詞與輸出較短、請求量較高的情境。",
      },
      {
        title: "開放權重與高性價比模型",
        rationale: "適合更重視成本而非最高效能的團隊的開放權重及高性價比選項。",
        workloadNote: "預設用量假設為對成本敏感的編碼或批次處理情境。",
      },
    ],
  },
  ko: {
    kicker: "엄선된 비교",
    title: "AI 모델 비교",
    intro: "미리 준비된 비교로 시작하거나 가격표에서 직접 비교를 만들 수 있습니다.",
    customTitle: "맞춤 비교 만들기",
    customBody: "가격표에서 모델 2~3개를 선택하면 나만의 사용량을 담은 공유 링크를 만들 수 있습니다.",
    customCta: "모델 선택",
    unavailableNotice: "현재 카탈로그에 포함된 모델이 2개 미만이라 이 비교는 일시적으로 사용할 수 없습니다.",
    missingModelsNotice: "현재 카탈로그에 없어 아래에서 숨겨짐",
    costDifferenceLabel: "이 워크로드에서의 비용 차이",
    limitationsTitle: "제한 사항",
    versionNotice: "이 비교의 모델 ID는 카탈로그와 정확히 일치하지만, 공급자의 엔드포인트 ID 뒤에 있는 실제 모델 버전이 고정되어 있음을 보장하지는 않습니다 \u2014 공급자는 ID를 유지한 채 제공 내용을 변경할 수 있습니다. 가격은 현재 카탈로그 스냅샷을 반영하며, 검증된 품질이나 지연 시간 순위가 아닙니다.",
    comparisons: [
      {
        title: "플래그십 추론 모델",
        rationale: "강력한 추론이 필요한 프로덕션 워크로드를 위한 고성능 범용 모델입니다.",
        workloadNote: "기본 워크로드는 추론 중심 어시스턴트와 적당한 프롬프트 캐싱을 가정합니다.",
      },
      {
        title: "저비용 모델",
        rationale: "분류나 짧은 응답처럼 대량이고 지연에 민감한 작업에 적합한 저비용 모델입니다.",
        workloadNote: "기본 워크로드는 짧은 프롬프트와 출력을 가진 높은 요청량을 가정합니다.",
      },
      {
        title: "오픈 웨이트 및 가성비 모델",
        rationale: "최고 성능보다 비용을 우선하는 팀을 위한 오픈 웨이트 및 가성비 옵션입니다.",
        workloadNote: "기본 워크로드는 비용에 민감한 코딩 또는 배치 처리를 가정합니다.",
      },
    ],
  },
};

export function compareDirectoryCopy(language: Language): CompareDirectoryCopy {
  return copy[language];
}
